/**
 * Step 7: Budget and Timeline
 * Collects budget constraints and implementation timeline preferences
 */

import React from 'react';
import { DollarSign, Clock, Calculator, Users, Mail, UserPlus, Trash2 } from 'lucide-react';
import { StepComponentProps } from '../../../../../types/onboarding';
import type { Stakeholder } from '../../../../../types/onboarding';
import { InfoTooltip } from '../../../../shared/tooltip/InfoTooltip';
import { getTooltipContent } from '../../../../../constants/onboarding';

export const Step7BudgetTimeline: React.FC<StepComponentProps> = ({
  formData,
  errors,
  onFieldChange,
  progressiveDisclosure,
}) => {
  // Check if stakeholder collection should be shown
  const showStakeholderCollection =
    formData.decisionProcess &&
    ['Board approval required', 'Multiple stakeholders', 'Committee decision'].includes(
      formData.decisionProcess as string,
    );

  // Calculate dynamic budget ranges based on community size
  const calculateDynamicBudgetRanges = () => {
    const totalUnits = parseInt(formData.totalUnits) || 406; // Default to 406 if not provided

    // Base pricing per unit (from Montana constants)
    const silverRate = 5; // $5/unit/month
    const goldRate = 8; // $8/unit/month

    // Calculate base monthly costs
    const silverMonthly = totalUnits * silverRate;
    const goldMonthly = totalUnits * goldRate;

    // Create ranges with padding for additional services, integrations, support, etc.
    // Real-world implementations typically cost 20-50% more than base pricing
    const padding = 0.3; // 30% padding for real-world costs

    const range1Start = Math.round(silverMonthly);
    const range1End = Math.round(silverMonthly * (1 + padding));

    const range2Start = range1End;
    const range2End = Math.round(goldMonthly);

    const range3Start = range2End;
    const range3End = Math.round(goldMonthly * (1 + padding));

    const range4Start = range3End;
    const range4End = Math.round(goldMonthly * (1 + padding * 2));

    return [
      {
        value: 'essential',
        label: `$${range1Start.toLocaleString()}-${range1End.toLocaleString()}/month`,
        description: 'Essential platform with core community management features',
        basePrice: silverMonthly,
      },
      {
        value: 'standard',
        label: `$${range2Start.toLocaleString()}-${range2End.toLocaleString()}/month`,
        description: 'Standard platform with enhanced features and integrations',
        basePrice: goldMonthly * 0.8,
      },
      {
        value: 'comprehensive',
        label: `$${range3Start.toLocaleString()}-${range3End.toLocaleString()}/month`,
        description: 'Comprehensive solution with advanced features and support',
        basePrice: goldMonthly,
      },
      {
        value: 'enterprise',
        label: `$${range4Start.toLocaleString()}-${range4End.toLocaleString()}/month`,
        description: 'Enterprise-grade with premium integrations and custom features',
        basePrice: goldMonthly * 1.3,
      },
      {
        value: 'custom',
        label: `$${range4End.toLocaleString()}+/month`,
        description: 'Custom solutions for large-scale or complex operations',
        basePrice: goldMonthly * 1.6,
      },
      {
        value: 'flexible',
        label: 'Flexible based on value delivered',
        description: 'Focus on ROI and community-specific requirements',
        basePrice: goldMonthly,
      },
    ];
  };

  const dynamicBudgetOptions = calculateDynamicBudgetRanges();

  // Stakeholder management functions
  const addStakeholder = () => {
    const currentStakeholders = formData.keyStakeholders || [];
    onFieldChange('keyStakeholders', [...currentStakeholders, { title: '', name: '', email: '' }]);
  };

  const removeStakeholder = (index: number) => {
    const currentStakeholders = formData.keyStakeholders || [];
    onFieldChange(
      'keyStakeholders',
      currentStakeholders.filter((_, i) => i !== index),
    );
  };

  const updateStakeholder = (index: number, field: keyof Stakeholder, value: string) => {
    const currentStakeholders = formData.keyStakeholders || [];
    const updated = currentStakeholders.map((stakeholder, i) =>
      i === index ? { ...stakeholder, [field]: value } : stakeholder,
    );
    onFieldChange('keyStakeholders', updated);
  };
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <DollarSign className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Timeline & Decision Process</h2>
        <p className="text-lg text-slate-600">
          Help us understand your timeline and decision-making process
        </p>
      </div>

      <div className="space-y-6">
        {/* Budget Range */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <label className="block text-lg font-semibold text-slate-800">
              Monthly Budget Range *
            </label>
            <InfoTooltip content="Budget ranges are calculated based on your community size using industry-standard pricing: Essential ($5/unit), Standard-Gold ($8/unit), with additional padding for integrations and support services." />
          </div>
          <p className="text-sm text-slate-600 mb-4">
            What's your comfortable monthly budget for community management software?
            {formData.totalUnits && (
              <span className="font-medium">
                {' '}
                Pricing calculated for your {parseInt(formData.totalUnits).toLocaleString()}-unit
                community.
              </span>
            )}
            {!formData.totalUnits && (
              <span className="text-amber-600">
                {' '}
                (Ranges shown are estimates - provide community size in Step 1 for accurate pricing)
              </span>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dynamicBudgetOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-start p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name="budgetRange"
                  value={option.value}
                  checked={formData.budgetRange === option.value}
                  onChange={(e) => onFieldChange('budgetRange', e.target.value)}
                  className="mt-1 mr-3 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <div className="font-medium text-slate-800">{option.label}</div>
                  <div className="text-sm text-slate-600">{option.description}</div>
                  {formData.totalUnits && (
                    <div className="text-xs text-slate-500 mt-1">
                      Based on {parseInt(formData.totalUnits).toLocaleString()} units
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
          {errors.budgetRange && <p className="mt-1 text-sm text-red-600">{errors.budgetRange}</p>}
        </div>

        {/* Implementation Timeline */}
        <div>
          <label className="block text-lg font-semibold text-slate-800 mb-3">
            Implementation Timeline *
          </label>
          <p className="text-sm text-slate-600 mb-4">
            When do you need to have a new system fully operational?
          </p>
          <div className="grid grid-cols-1 gap-3">
            {[
              {
                value: 'immediate',
                label: 'Immediate (Within 1-2 weeks)',
                description: 'Current system failing, urgent replacement needed',
              },
              {
                value: 'very-soon',
                label: 'Very Soon (Within 1 month)',
                description: 'High priority, ready to move quickly',
              },
              {
                value: 'soon',
                label: 'Soon (1-3 months)',
                description: 'Important project, timeline driven by specific need',
              },
              {
                value: 'planning',
                label: 'Planning Phase (3-6 months)',
                description: 'Evaluating options, flexible on timing',
              },
              {
                value: 'future',
                label: 'Future Consideration (6+ months)',
                description: 'Early research, no immediate pressure',
              },
            ].map((option) => (
              <label
                key={option.value}
                className="flex items-start p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name="implementationTimeline"
                  value={option.value}
                  checked={formData.implementationTimeline === option.value}
                  onChange={(e) => onFieldChange('implementationTimeline', e.target.value)}
                  className="mt-1 mr-3 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <div className="font-medium text-slate-800">{option.label}</div>
                  <div className="text-sm text-slate-600">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
          {errors.implementationTimeline && (
            <p className="mt-1 text-sm text-red-600">{errors.implementationTimeline}</p>
          )}
        </div>

        {/* What's Driving Timeline */}
        <div>
          <label className="block text-lg font-semibold text-slate-800 mb-3">
            What's Driving Your Timeline? *
          </label>
          <p className="text-sm text-slate-600 mb-4">
            Understanding what's motivating your timeline helps us prioritize accordingly
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Current system failing/inadequate',
              'Contract renewal deadline',
              'Board election/transition',
              'Fiscal year budget cycle',
              'New management company',
              'Community growth requiring better tools',
              'Compliance requirements',
              'No specific deadline - exploring options',
            ].map((option) => (
              <label
                key={option}
                className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={(formData.timelineDrivers as string[])?.includes(option) || false}
                  onChange={(e) => {
                    const current = (formData.timelineDrivers as string[]) || [];
                    if (e.target.checked) {
                      onFieldChange('timelineDrivers', [...current, option]);
                    } else {
                      onFieldChange(
                        'timelineDrivers',
                        current.filter((d) => d !== option),
                      );
                    }
                  }}
                  className="mr-3 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-slate-700">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Decision Making Process */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Decision Making Process *
            <InfoTooltip
              title="Decision Making Process"
              content="Understanding who's involved in the final decision helps us tailor our approach and ensure all stakeholders get the information they need."
              iconSize={16}
            />
          </label>
          <p className="text-sm text-slate-600 mb-4">Who else is involved in this decision?</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              {
                value: 'I make the decision alone',
                description:
                  'You have full authority to make this purchasing decision independently',
              },
              {
                value: 'Board approval required',
                description: 'Formal vote by the HOA/community board is needed for final approval',
              },
              {
                value: 'Management company involved',
                description: 'Property management company needs to review and approve the decision',
              },
              {
                value: 'Committee decision',
                tooltip: 'decision-committee',
              },
              {
                value: 'Resident input needed',
                description: 'Community residents need to provide input or vote on the decision',
              },
              {
                value: 'Multiple stakeholders',
                tooltip: 'decision-multiple-stakeholders',
              },
            ].map((option) => (
              <label
                key={option.value}
                className="flex items-start p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                title={option.description || ''}
              >
                <input
                  type="radio"
                  name="decisionProcess"
                  value={option.value}
                  checked={formData.decisionProcess === option.value}
                  onChange={(e) => onFieldChange('decisionProcess', e.target.value)}
                  className="mt-1 mr-3 text-emerald-600 focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-700 font-medium">{option.value}</span>
                    {option.tooltip && (
                      <InfoTooltip
                        {...getTooltipContent(option.tooltip)}
                        content={
                          getTooltipContent(option.tooltip)?.content ||
                          option.description ||
                          'Additional information'
                        }
                        iconSize={14}
                      />
                    )}
                  </div>
                  {option.description && (
                    <div className="text-xs text-slate-500 mt-1">{option.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
          {errors.decisionProcess && (
            <p className="mt-1 text-sm text-red-600">{errors.decisionProcess}</p>
          )}
        </div>

        {/* Stakeholder Collection - Conditional */}
        {showStakeholderCollection && (
          <div>
            <label className="block text-lg font-semibold text-slate-800 mb-3">
              Key Stakeholders
            </label>
            <p className="text-sm text-slate-600 mb-4">
              Who are the key people involved in this decision? We'll send them a summary after your
              demo.
            </p>

            {/* Existing Stakeholders */}
            {formData.keyStakeholders && formData.keyStakeholders.length > 0 && (
              <div className="space-y-3 mb-4">
                {formData.keyStakeholders.map((stakeholder, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-600" />
                        <span className="font-medium text-slate-700">Stakeholder {index + 1}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeStakeholder(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remove stakeholder"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Title/Role *
                        </label>
                        <input
                          type="text"
                          value={stakeholder.title}
                          onChange={(e) => updateStakeholder(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="e.g., Board President"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          value={stakeholder.name}
                          onChange={(e) => updateStakeholder(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Full name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <input
                            type="email"
                            value={stakeholder.email}
                            onChange={(e) => updateStakeholder(index, 'email', e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="email@example.com"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Stakeholder Button */}
            <button
              type="button"
              onClick={addStakeholder}
              className="w-full border-2 border-dashed border-emerald-300 rounded-lg p-4 text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Add Stakeholder
            </button>

            {/* Info about stakeholder emails */}
            <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-700">
                <strong>📧 Stakeholder Notification:</strong> After your demo, we'll automatically
                send a summary of our discussion and next steps to all stakeholders listed above.
              </p>
            </div>

            {errors.keyStakeholders && (
              <p className="mt-1 text-sm text-red-600">{errors.keyStakeholders}</p>
            )}
          </div>
        )}

        {/* Competitive Analysis */}
        <div>
          <label className="block text-lg font-semibold text-slate-800 mb-4">
            Current Solution Evaluation *
          </label>
          <p className="text-sm text-slate-600 mb-4">
            Are you currently evaluating other solutions or platforms?
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Evaluation Status *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Community App is our first evaluation',
                  'Currently comparing 2-3 solutions',
                  'Deep evaluation of multiple platforms',
                  'Comparison shopping for best value',
                ].map((status) => (
                  <label
                    key={status}
                    className="flex items-center p-3 border rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="competitiveAnalysis"
                      value={status}
                      checked={formData.competitiveAnalysis === status}
                      onChange={(e) => onFieldChange('competitiveAnalysis', e.target.value)}
                      className="mr-3 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Decision Timeline (Optional)
              </label>
              <input
                type="text"
                value={(formData.decisionTimeline as string) || ''}
                onChange={(e) => onFieldChange('decisionTimeline', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="e.g., Need to decide within 3 months for budget cycle"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Key Evaluation Criteria *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  'Cost/ROI',
                  'Ease of Use',
                  'Implementation Speed',
                  'Feature Completeness',
                  'Integration Capabilities',
                  'Customer Support',
                  'Security & Compliance',
                  'Mobile Experience',
                  'Customization Options',
                  'Vendor Reputation',
                  'Resident Experience',
                  'Transparency Features',
                  'Board Collaboration Tools',
                  'Training & Adoption Support',
                ].map((criteria) => (
                  <label key={criteria} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={
                        (formData.evaluationCriteria as string[])?.includes(criteria) || false
                      }
                      onChange={(e) => {
                        const current = (formData.evaluationCriteria as string[]) || [];
                        if (e.target.checked) {
                          onFieldChange('evaluationCriteria', [...current, criteria]);
                        } else {
                          onFieldChange(
                            'evaluationCriteria',
                            current.filter((c) => c !== criteria),
                          );
                        }
                      }}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700">{criteria}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Budget Flexibility */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Budget Flexibility *
            <InfoTooltip
              {...getTooltipContent('budget-flexibility')}
              content={
                getTooltipContent('budget-flexibility')?.content ||
                'Information about budget flexibility'
              }
              iconSize={16}
            />
          </label>
          <p className="text-sm text-slate-600 mb-4">
            How flexible is your budget for the right solution?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Very rigid - must stay within range',
              'Somewhat flexible - up to 25% more for value',
              'Flexible - willing to pay more for ROI',
              'Very flexible - we have the budget for the right solution',
            ].map((option) => (
              <label
                key={option}
                className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name="budgetFlexibility"
                  value={option}
                  checked={formData.budgetFlexibility === option}
                  onChange={(e) => onFieldChange('budgetFlexibility', e.target.value)}
                  className="mr-3 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-slate-700">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional Budget Notes */}
        <div>
          <label htmlFor="budgetNotes" className="block text-lg font-semibold text-slate-800 mb-3">
            Additional Budget Considerations
          </label>
          <p className="text-sm text-slate-600 mb-4">
            Any special budget constraints, approval processes, or considerations?
          </p>
          <textarea
            id="budgetNotes"
            value={(formData.budgetNotes as string) || ''}
            onChange={(e) => onFieldChange('budgetNotes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="e.g., Annual vs monthly payments, trial periods needed, budget cycles, etc."
          />
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-emerald-50 rounded-lg p-6 mt-6">
        <div className="flex items-start space-x-3">
          <Calculator className="w-6 h-6 text-emerald-600 mt-1" />
          <div>
            <h3 className="font-semibold text-emerald-900 mb-2">Flexible Payment Options</h3>
            <ul className="text-sm text-emerald-800 space-y-1">
              <li>• Monthly, quarterly, or annual payment plans available</li>
              <li>• Free trial periods to validate fit before commitment</li>
              <li>• Transparent pricing with no hidden fees</li>
              <li>• Custom enterprise pricing for unique requirements</li>
              <li>• ROI calculator to demonstrate value proposition</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
